import { __decorate } from "tslib";
import { Component, } from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { action, computed, observable } from 'mobx';
import omit from 'lodash/omit';
import defer from 'lodash/defer';
import noop from 'lodash/noop';
import classes from 'component-classes';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import autobind from '../_util/autobind';
import { Size } from './enum';
import normalizeLanguage from '../_util/normalizeLanguage';
import localeContext from '../locale-context';
/**
 * Code生成器
 */
const CodeGen = (function* (start) {
    while (true) {
        yield `anonymous-${++start}`;
    }
})(1000);
/* eslint-disable react/no-unused-prop-types */
export default class ViewComponent extends Component {
    constructor(props, context) {
        super(props, context);
        this.setCode(props);
        this.setObservableProps(props, context);
        this.prefixCls = this.getContextProPrefixCls(props.suffixCls, props.prefixCls);
    }
    static get contextType() {
        return ConfigContext;
    }
    get lang() {
        const { lang } = this.observableProps;
        if (lang) {
            return lang;
        }
        return localeContext.locale.lang;
    }
    get disabled() {
        return this.isDisabled();
    }
    setCode(props) {
        this.code = props.code || CodeGen.next().value;
    }
    getMergedClassNames(...props) {
        return classNames(this.getClassName(), this.getWrapperClassNames(), ...props);
    }
    getMergedProps(props = {}) {
        const wrapperProps = this.getWrapperProps(props);
        const otherProps = this.getOtherProps();
        const mergedProps = {
            ...wrapperProps,
            ...otherProps,
            className: classNames(wrapperProps.className, otherProps.className),
        };
        if (wrapperProps.style && otherProps.style) {
            mergedProps.style = { ...wrapperProps.style, ...otherProps.style };
        }
        return mergedProps;
    }
    getObservableProps(props, _context) {
        return {
            lang: props.lang,
            disabled: props.disabled,
        };
    }
    setObservableProps(props, context) {
        this.observableProps = this.getObservableProps(props, context);
    }
    updateObservableProps(props, context) {
        Object.assign(this.observableProps, this.getObservableProps(props, context));
    }
    getOmitPropsKeys() {
        const keys = [
            'code',
            'prefixCls',
            'suffixCls',
            'className',
            'elementClassName',
            'style',
            'size',
            'autoFocus',
            'onFocus',
            'onBlur',
            'children',
            'dataSet',
        ];
        if (this.disabled) {
            return keys.concat([
                'onClick',
                'onMouseUp',
                'onMouseDown',
                'onMouseEnter',
                'onMouseLeave',
                // 'onMouseOver',
                // 'onMouseOut',
                'onKeyDown',
                'onKeyUp',
                'onKeyPress',
                'onContextMenu',
            ]);
        }
        return keys;
    }
    getOtherProps() {
        const { props, disabled } = this;
        const { tabIndex, lang, style = {} } = props;
        const otherProps = omit(props, this.getOmitPropsKeys());
        if (disabled) {
            if (tabIndex !== undefined) {
                otherProps.tabIndex = -1;
            }
        }
        else {
            otherProps.onFocus = this.handleFocus;
            otherProps.onBlur = this.handleBlur;
        }
        otherProps.ref = this.elementReference;
        otherProps.disabled = disabled;
        otherProps.className = this.getClassName();
        otherProps.style = {};
        if (this.height) {
            otherProps.style.height = this.height;
        }
        else if ('height' in style) {
            otherProps.style.height = style.height;
        }
        if ('minHeight' in style) {
            otherProps.style.minHeight = style.minHeight;
        }
        if ('maxHeight' in style) {
            otherProps.style.maxHeight = style.maxHeight;
        }
        otherProps.lang = normalizeLanguage(lang);
        return otherProps;
    }
    getClassName(...props) {
        const { prefixCls, props: { elementClassName }, } = this;
        return classNames(prefixCls, elementClassName, ...props);
    }
    getWrapperProps(props = {}) {
        const { style, hidden, onMouseEnter, onMouseLeave } = this.props;
        const mergedStyle = props.style || style;
        const wrapperProps = {
            ref: this.wrapperReference,
            className: this.getWrapperClassNames(),
            hidden,
            ...props,
        };
        if (this.disabled) {
            if (!wrapperProps.onMouseEnter) {
                wrapperProps.onMouseEnter = onMouseEnter;
            }
            if (!wrapperProps.onMouseLeave) {
                wrapperProps.onMouseLeave = onMouseLeave;
            }
        }
        if (mergedStyle) {
            wrapperProps.style = omit(mergedStyle, ['height', 'minHeight', 'maxHeight']);
        }
        return wrapperProps;
    }
    getWrapperClassNames(...args) {
        const { prefixCls, props: { className, size }, } = this;
        return classNames(`${prefixCls}-wrapper`, className, {
            [`${prefixCls}-sm`]: size === Size.small,
            [`${prefixCls}-lg`]: size === Size.large,
            [`${prefixCls}-disabled`]: this.disabled,
            [`${prefixCls}-focused`]: this.useFocusedClassName() && this.isFocus,
        }, ...args);
    }
    isDisabled() {
        const { disabled = false } = this.observableProps;
        return disabled;
    }
    useFocusedClassName() {
        return true;
    }
    handleFocus(e) {
        this.isFocused = true;
        this.isFocus = true;
        const { props: { onFocus = noop }, prefixCls, } = this;
        if (this.useFocusedClassName()) {
            const element = this.wrapper || findDOMNode(this);
            if (element) {
                classes(element).add(`${prefixCls}-focused`);
            }
        }
        onFocus(e);
    }
    forceBlur(e) {
        const { props: { onBlur = noop }, } = this;
        onBlur(e);
    }
    handleBlur(e) {
        if (!e.isDefaultPrevented()) {
            const { prefixCls } = this;
            this.forceBlur(e);
            if (!e.isDefaultPrevented()) {
                this.isFocused = false;
                this.isFocus = false;
                if (this.useFocusedClassName()) {
                    const element = this.wrapper || findDOMNode(this);
                    if (element) {
                        classes(element).remove(`${prefixCls}-focused`);
                    }
                }
            }
        }
    }
    focus() {
        if (this.element) {
            this.element.focus();
        }
    }
    blur() {
        if (this.element) {
            this.element.blur();
        }
    }
    elementReference(node) {
        this.element = node;
    }
    wrapperReference(node) {
        this.wrapper = node;
    }
    componentWillReceiveProps(nextProps, nextContext) {
        const { code, prefixCls } = this.props;
        const { prefixCls: nextPrefixCls } = nextProps;
        if (prefixCls !== nextPrefixCls) {
            this.prefixCls = this.getContextProPrefixCls(nextProps.suffixCls, nextPrefixCls);
        }
        if (nextProps.code !== code) {
            this.setCode(nextProps);
        }
        this.updateObservableProps(nextProps, nextContext);
    }
    componentDidMount() {
        const { tabIndex, autoFocus } = this.props;
        if (autoFocus && (tabIndex === undefined || tabIndex > -1) && !this.disabled) {
            defer(() => this.focus());
        }
    }
    setHeight(height) {
        this.height = height;
        const { element } = this;
        if (element) {
            element.style.height = height;
        }
    }
    getContextConfig(key) {
        const { context } = this;
        return context.getConfig(key);
    }
    getContextProPrefixCls(suffixCls, customizePrefixCls) {
        const { context } = this;
        return context.getProPrefixCls(suffixCls, customizePrefixCls);
    }
}
ViewComponent.propTypes = {
    /**
     * 组件id
     */
    id: PropTypes.string,
    /**
     * 组件大小<未实现>
     * 可选值 `default` `small` `big`
     */
    size: PropTypes.oneOf([Size.small, Size.default, Size.large]),
    /**
     * 样式后缀
     */
    suffixCls: PropTypes.string,
    /**
     * 样式前缀
     */
    prefixCls: PropTypes.string,
    /**
     * 悬浮提示，建议用ToolTip组件
     */
    title: PropTypes.node,
    /**
     *  是否禁用
     */
    disabled: PropTypes.bool,
    /**
     * 是否隐藏
     */
    hidden: PropTypes.bool,
    /**
     * 自动获取焦点，多个组件同时设置该参数时，以节点树的顺序最末的组件获取焦点
     */
    autoFocus: PropTypes.bool,
    /**
     * 快捷键, 通过 Alt + accessKey (或者 Shift + Alt + accessKey) 使组件聚焦
     */
    accessKey: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    /**
     * 内容的文本方向
     */
    dir: PropTypes.string,
    /**
     * 内容可编辑
     */
    contentEditable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    /**
     * 是否可拖动
     */
    draggable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    /**
     * 内链样式
     */
    style: PropTypes.object,
    /**
     * 自定义样式名
     */
    className: PropTypes.string,
    /**
     * 键盘Tab键焦点序号，设为-1时不会获得焦点，设为0时为节点树的顺序。
     */
    tabIndex: PropTypes.number,
    /**
     * 语言
     */
    lang: PropTypes.string,
    /**
     * 拼写校验
     */
    spellCheck: PropTypes.bool,
    /**
     * 获取焦点回调
     */
    onFocus: PropTypes.func,
    /**
     * 失去焦点回调
     */
    onBlur: PropTypes.func,
    /**
     * 单击回调
     */
    onClick: PropTypes.func,
    /**
     * 双击回调
     */
    onDoubleClick: PropTypes.func,
    /**
     * 鼠标抬起回调
     */
    onMouseUp: PropTypes.func,
    /**
     * 鼠标点下回调
     */
    onMouseDown: PropTypes.func,
    /**
     * 鼠标移动回调
     */
    onMouseMove: PropTypes.func,
    /**
     * 鼠标进入回调
     */
    onMouseEnter: PropTypes.func,
    /**
     * 鼠标离开回调
     */
    onMouseLeave: PropTypes.func,
    /**
     * 鼠标进入回调，与onMouseEnter区别在于鼠标进入子节点时会触发onMouseOut
     */
    onMouseOver: PropTypes.func,
    /**
     * 鼠标离开回调，与onMouseLeave区别在于子节点的onMouseout会冒泡触发本回调
     */
    onMouseOut: PropTypes.func,
    /**
     * 鼠标右击后的回调
     */
    onContextMenu: PropTypes.func,
    /**
     * 键盘按下时的回调
     */
    onKeyDown: PropTypes.func,
    /**
     * 键盘抬起时的回调
     */
    onKeyUp: PropTypes.func,
    /**
     * 键盘敲击后的回调
     */
    onKeyPress: PropTypes.func,
};
__decorate([
    observable
], ViewComponent.prototype, "isFocused", void 0);
__decorate([
    observable
], ViewComponent.prototype, "observableProps", void 0);
__decorate([
    computed
], ViewComponent.prototype, "lang", null);
__decorate([
    computed
], ViewComponent.prototype, "disabled", null);
__decorate([
    action
], ViewComponent.prototype, "setObservableProps", null);
__decorate([
    action
], ViewComponent.prototype, "updateObservableProps", null);
__decorate([
    autobind,
    action
], ViewComponent.prototype, "handleFocus", null);
__decorate([
    autobind,
    action
], ViewComponent.prototype, "handleBlur", null);
__decorate([
    autobind
], ViewComponent.prototype, "elementReference", null);
__decorate([
    autobind,
    action
], ViewComponent.prototype, "wrapperReference", null);
__decorate([
    autobind
], ViewComponent.prototype, "getContextConfig", null);
__decorate([
    autobind
], ViewComponent.prototype, "getContextProPrefixCls", null);
